/**
 * db.ts
 *
 * IndexedDB 離線存儲 Schema 定義與操作輔助函數。
 * 使用 `idb` 庫封裝 IndexedDB，提供類型安全的存儲訪問。
 *
 * Object Stores:
 * - outbox       : 離線操作佇列（待同步的 HTTP 請求）
 * - cache_cases  : 已快取的案件資料
 * - cache_forms  : 已快取的表單模板
 * - cache_media  : 已快取的媒體檔案（照片、文件等）
 * - cache_tasks  : 已下載的稽核任務離線包 manifest
 * - cache_api    : 任務包內的 API JSON 快照
 */
import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

/** IndexedDB 資料庫名稱 */
const DB_NAME = 'zhcert_offline';

/** IndexedDB 資料庫版本（升版需在 upgrade 中處理遷移） */
const DB_VERSION = 3;

// ─────────────────────────────────────────────
// 資料類型定義
// ─────────────────────────────────────────────

/**
 * 離線操作佇列條目。
 * 每筆代表一個尚未同步到後端的 HTTP 請求。
 */
export interface OutboxEntry {
  /** UUID v4，唯一標識符 */
  id: string;
  /** HTTP 動詞 */
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** 目標 API 路徑，例如 /api/cases/1/findings */
  url: string;
  /** 請求 Body（JSON 可序列化的資料） */
  body?: unknown;
  /** 附加 HTTP headers */
  headers?: Record<string, string>;
  /** 已重試次數 */
  attempts: number;
  /** 最後一次錯誤訊息 */
  lastError?: string;
  /** 建立時間戳（Unix ms） */
  createdAt: number;
  /** 當前同步狀態 */
  status: 'pending' | 'inflight' | 'failed';
  /** 關聯實體類型（如 'finding'、'checklist_item'），用於衝突追蹤 */
  entityType?: string;
  /** 關聯實體的本地臨時 ID（離線建立時尚無服務端 ID） */
  entityClientLocalId?: string;
  /** 關聯稽核任務 ID（離線管理頁統計用，不會送到 API body） */
  taskId?: number;
  /** 關聯案件 ID（離線管理頁統計用，不會送到 API body） */
  caseId?: number;
}

/**
 * 快取的案件資料。
 * 供離線時查詢案件詳情使用。
 */
export interface CachedCase {
  /** 服務端案件 ID（IndexedDB key） */
  caseId: number;
  /** 案件編號，如 CASE-2025-0001 */
  caseCode: string;
  /** 完整案件資料（來自 API 的原始 JSON） */
  data: unknown;
  /** 快取時間戳（Unix ms） */
  cachedAt: number;
}

/**
 * 快取的表單模板（DOCX + JSON Schema）。
 * 供離線填寫表單使用。
 */
export interface CachedForm {
  /** 模板版本 ID（IndexedDB key） */
  templateVersionId: number;
  /** 表單 JSON Schema（用於離線渲染） */
  schemaJson: unknown;
  /** 關聯的 DOCX 檔案 ID（null 表示純線上模板） */
  docxFileId: number | null;
  /** 快取時間戳（Unix ms） */
  cachedAt: number;
}

/**
 * 媒体檔案缓存 / 暂存（合并 stage 2 离线上传 + stage 3 离线下载）。
 *
 * id 命名空間（避免冲突）：
 *   'pending:<tempId>' — 阶段 2：离线暂存等待上传，tempId 是负数
 *   'file:<realId>'    — 阶段 3：从服务器预下载的只读缓存，realId 是正数
 *
 * 状态机（status）：
 *   pending   离线暂存，等待 worker flush
 *   uploading worker 正在处理（防并发）
 *   uploaded  上传成功，realId 已存入 uploadedFileId（可清理）
 *   cached    阶段 3：从服务器预下载的离线只读（可被 eviction 安全删除——可重取）
 *   local     用户产生的本地 only 副本（如 stage-1 field-finding 照片）；
 *             worker 不 flush，eviction 不 evict——这是用户唯一的副本
 *   error     重试上限耗尽，等待用户操作
 */
export interface CachedMedia {
  /** 'pending:<negative tempId>' 或 'file:<positive realId>' */
  id: string;
  /** 媒体二进位资料 */
  blob: Blob;
  /** 原始文件名 */
  filename: string;
  /** MIME 類型，如 image/jpeg */
  mimeType: string;
  /** 文件大小（位元组） */
  sizeBytes: number;
  /** 業務分類（如 'form-attachment'/'field-finding'）；string 以容纳未来扩展 */
  scope: string;
  /** 关联实体类型（如 'form_instance'） */
  ownerType?: string;
  /** 关联实体 ID（如 form_instance.id） */
  ownerId?: number;
  /** 上传 / 缓存状态机 */
  status: 'pending' | 'uploading' | 'uploaded' | 'cached' | 'local' | 'error';
  /** 上传成功后的真实 fileId（pending/uploading/error 时为 undefined） */
  uploadedFileId?: number;
  /** 重试次数（指数退避：2s/8s/30s） */
  retryCount: number;
  /** 最后一次失败的错误说明 */
  errorMessage?: string;
  /** 最后一次尝试时间戳（用于退避控制） */
  lastAttemptAt?: number;
  /** 创建時间戳（Unix ms） */
  createdAt: number;
}

/**
 * 已下載的稽核任務離線包 manifest。
 *
 * task detail 的完整 JSON 放在 data；其他 API JSON 放 cache_api，二進位放 cache_media。
 */
export interface CachedTaskPack {
  /** audit_tasks.id（IndexedDB key） */
  taskId: number;
  /** cases.id */
  caseId: number;
  /** clients.id；任務缺客戶時為 null */
  clientId: number | null;
  /** 案件編號快照 */
  caseCode: string | null;
  /** 客戶名稱快照 */
  clientName: string | null;
  /** 離線包完整性狀態 */
  status: 'ready' | 'partial' | 'quota_limited' | 'error';
  /** GET /api/audit-tasks/:id 的 task JSON */
  data: unknown;
  /** 下載完成/更新時間戳（Unix ms） */
  downloadedAt: number;
  /** 最後更新時間戳（Unix ms） */
  updatedAt: number;
  /** 已下載二進位估算大小 */
  sizeBytes: number;
  /** 表單數 */
  formsCount: number;
  /** 媒體資源數 */
  mediaCount: number;
  /** API 快照數 */
  apiCount: number;
  /** 本次下載錯誤數 */
  errorCount: number;
}

/** 任務包內單一資源狀態，用於 UI 顯示完整度與清理。 */
export interface CachedTaskResource {
  /** `${taskId}:${resourceType}:${resourceId}` */
  id: string;
  taskId: number;
  resourceType:
    | 'task'
    | 'case'
    | 'client'
    | 'client-land'
    | 'client-processing-site'
    | 'client-case'
    | 'form-instance'
    | 'form-template'
    | 'case-document'
    | 'form-template-docx'
    | 'form-attachment'
    | 'finding'
    | 'sample'
    | 'laboratory-option';
  resourceId: string;
  status: 'cached' | 'missing' | 'error';
  sizeBytes?: number;
  errorMessage?: string;
  cachedAt: number;
}

/** API JSON 快照，供離線頁面做 online-first fallback。 */
export interface CachedApiResponse {
  /** API path（IndexedDB key），例如 /api/clients/1/lands */
  url: string;
  /** 關聯 task id；全域字典可不填 */
  taskId?: number;
  /** 業務分類：task-detail / case-detail / client-profile / field-data 等 */
  scope: string;
  /** API response data（已拆掉 success envelope 後的 data） */
  data: unknown;
  /** 快取時間戳（Unix ms） */
  cachedAt: number;
}

// ─────────────────────────────────────────────
// DBSchema 定義
// ─────────────────────────────────────────────

/**
 * idb 類型安全 Schema：定義所有 Object Store 的 key 類型、value 類型及 index。
 */
interface Schema extends DBSchema {
  outbox: {
    key: string;
    value: OutboxEntry;
    indexes: {
      /** 按同步狀態查詢（pending / inflight / failed） */
      'by-status': string;
      /** 按實體類型分組（用於衝突偵測） */
      'by-entity': string;
    };
  };
  cache_cases: {
    key: number;
    value: CachedCase;
  };
  cache_forms: {
    key: number;
    value: CachedForm;
  };
  cache_media: {
    key: string;
    value: CachedMedia;
    indexes: {
      /** 按 uploadedFileId 反查（仅适用 status=uploaded/cached 行） */
      'by-uploaded': number;
      /** 按 status 查询 pending / error 列表 */
      'by-status': string;
    };
  };
  /** key-value metadata：tempId 計數器等单值持久化 */
  meta: {
    key: string;
    value: { value: number };
  };
  cache_tasks: {
    key: number;
    value: CachedTaskPack;
    indexes: {
      'by-case': number;
      'by-status': string;
    };
  };
  cache_task_resources: {
    key: string;
    value: CachedTaskResource;
    indexes: {
      'by-task': number;
      'by-status': string;
    };
  };
  cache_api: {
    key: string;
    value: CachedApiResponse;
    indexes: {
      'by-task': number;
      'by-scope': string;
    };
  };
}

// ─────────────────────────────────────────────
// DB 初始化（單例模式）
// ─────────────────────────────────────────────

/** DB 連接單例 Promise，避免重複 open */
let dbPromise: Promise<IDBPDatabase<Schema>> | null = null;

/**
 * 在原生 IDBDatabase 上应用 oldVersion → DB_VERSION 的升级步骤。
 * 被 getOfflineDb（idb wrapper）和 pushOutboxReliable（原生 open）共用，
 * 避免两条路径打开不同版本造成 onblocked 死锁（PR #2 review #4）。
 */
function applyOfflineDbUpgrade(rawDb: IDBDatabase, oldVersion: number): void {
  if (oldVersion < 1) {
    const outbox = rawDb.createObjectStore('outbox', { keyPath: 'id' });
    outbox.createIndex('by-status', 'status');
    outbox.createIndex('by-entity', 'entityType');

    rawDb.createObjectStore('cache_cases', { keyPath: 'caseId' });
    rawDb.createObjectStore('cache_forms', { keyPath: 'templateVersionId' });

    const media = rawDb.createObjectStore('cache_media', { keyPath: 'id' });
    media.createIndex('by-uploaded', 'uploadedFileId');
  }
  if (oldVersion < 2) {
    // 旧 cache_media 用 UUID 作 id 且字段不带 status，统一清空。
    // 生產環境此 store 尚未真正使用，可接受破坏性升级。
    if (rawDb.objectStoreNames.contains('cache_media')) {
      rawDb.deleteObjectStore('cache_media');
    }
    const media = rawDb.createObjectStore('cache_media', { keyPath: 'id' });
    media.createIndex('by-uploaded', 'uploadedFileId');
    media.createIndex('by-status', 'status');

    rawDb.createObjectStore('meta');
  }
  if (oldVersion < 3) {
    const tasks = rawDb.createObjectStore('cache_tasks', { keyPath: 'taskId' });
    tasks.createIndex('by-case', 'caseId');
    tasks.createIndex('by-status', 'status');

    const resources = rawDb.createObjectStore('cache_task_resources', { keyPath: 'id' });
    resources.createIndex('by-task', 'taskId');
    resources.createIndex('by-status', 'status');

    const api = rawDb.createObjectStore('cache_api', { keyPath: 'url' });
    api.createIndex('by-task', 'taskId');
    api.createIndex('by-scope', 'scope');
  }
}

/**
 * 取得 IndexedDB 連接（單例）。
 * 首次調用時執行 upgrade 建立 Object Store 與 Index。
 */
export function getOfflineDb(): Promise<IDBPDatabase<Schema>> {
  if (dbPromise) return dbPromise;
  dbPromise = openDB<Schema>(DB_NAME, DB_VERSION, {
    upgrade(db, oldVersion) {
      // idb 的 typed db 内部代理到原生 IDBDatabase，createObjectStore/createIndex
      // 直接命中原生 API，因此可与 pushOutboxReliable 走同一份升级函数。
      applyOfflineDbUpgrade(db as unknown as IDBDatabase, oldVersion);
    },
  });
  return dbPromise;
}

// ─────────────────────────────────────────────
// Outbox 操作函數
// ─────────────────────────────────────────────

/**
 * 將一筆離線操作加入佇列。
 * 自動生成 UUID、設置 createdAt、初始化 attempts=0 與 status='pending'。
 *
 * @param entry - 不含 id/createdAt/attempts/status 的操作資料
 * @returns 新條目的 UUID
 */
export async function pushOutbox(
  entry: Omit<OutboxEntry, 'id' | 'createdAt' | 'attempts' | 'status'>,
): Promise<string> {
  const db = await getOfflineDb();
  const id = crypto.randomUUID();
  await db.put('outbox', {
    ...entry,
    id,
    createdAt: Date.now(),
    attempts: 0,
    status: 'pending',
  });
  return id;
}

/**
 * 可靠写入一笔 outbox。
 *
 * 这条路径刻意使用浏览器原生 IndexedDB API，而不是 idb wrapper：
 * 离线提交按钮的 UX 不能被 React Query / wrapper 层的异常状态卡住。组件层统一调用
 * 此 helper，避免各自复制 indexedDB.open('zhcert_offline')。
 */
export function pushOutboxReliable(
  entry: Omit<OutboxEntry, 'id' | 'createdAt' | 'attempts' | 'status'>,
): Promise<string> {
  return new Promise((resolve, reject) => {
    // 显式带版本号 + onupgradeneeded：避免老版本连接阻塞 getOfflineDb 的 v3 升级（PR #2 review #4）
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => reject(request.error ?? new Error('Failed to open offline DB'));
    request.onblocked = () =>
      reject(new Error('Failed to open offline DB: blocked by another tab'));
    request.onupgradeneeded = (event) => {
      const rawDb = request.result;
      applyOfflineDbUpgrade(rawDb, event.oldVersion);
    };
    request.onsuccess = () => {
      const db = request.result;
      const id = crypto.randomUUID();
      const tx = db.transaction('outbox', 'readwrite');
      tx.objectStore('outbox').put({
        ...entry,
        body: withClientMutationId(entry.method, entry.body),
        id,
        createdAt: Date.now(),
        attempts: 0,
        status: 'pending',
      });
      tx.oncomplete = () => {
        db.close();
        resolve(id);
      };
      tx.onerror = () => {
        db.close();
        reject(tx.error ?? new Error('Failed to write offline outbox'));
      };
    };
  });
}

function withClientMutationId(method: OutboxEntry['method'], body: unknown): unknown {
  if (method !== 'POST' || !body || typeof body !== 'object' || Array.isArray(body)) return body;
  if ('clientMutationId' in body) return body;
  return { ...(body as Record<string, unknown>), clientMutationId: crypto.randomUUID() };
}

/**
 * 取得所有 status='pending' 的佇列條目（按 by-status index 查詢）。
 */
export async function listPendingOutbox(): Promise<OutboxEntry[]> {
  const db = await getOfflineDb();
  return db.getAllFromIndex('outbox', 'by-status', 'pending');
}

/**
 * 把所有 status='inflight' 的孤儿条目重置为 'pending'。
 *
 * claimPendingOutbox 在 fetch 之前就把行从 pending → inflight。如果 tab 在
 * fetch 完成前关闭/崩溃/被刷新，该行会卡在 inflight 永不重试；listPendingOutbox
 * 只看 pending，所以也不会被下一轮 sync 看到 (PR #2 review #5)。
 *
 * 启动时调一次本函数把孤儿全部捞回来。
 */
export async function reapInflightOutbox(): Promise<number> {
  const db = await getOfflineDb();
  const tx = db.transaction('outbox', 'readwrite');
  const store = tx.objectStore('outbox');
  const inflight = await store.index('by-status').getAll('inflight');
  for (const row of inflight) {
    await store.put({ ...row, status: 'pending' });
  }
  await tx.done;
  return inflight.length;
}

/**
 * 原子 claim 一笔 pending outbox。
 *
 * 多个同步入口或多个浏览器 tab 同时启动时，只有第一个把 status 从 pending 改成
 * inflight 的调用会拿到 entry；其他调用会返回 null，避免重复 POST。
 */
export async function claimPendingOutbox(id: string): Promise<OutboxEntry | null> {
  const db = await getOfflineDb();
  const tx = db.transaction('outbox', 'readwrite');
  const store = tx.objectStore('outbox');
  const cur = await store.get(id);
  if (!cur || cur.status !== 'pending') {
    await tx.done;
    return null;
  }
  const claimed: OutboxEntry = { ...cur, status: 'inflight' };
  await store.put(claimed);
  await tx.done;
  return claimed;
}

/**
 * 刪除指定 ID 的佇列條目（同步成功後呼叫）。
 *
 * @param id - 條目的 UUID
 */
export async function deleteOutbox(id: string): Promise<void> {
  const db = await getOfflineDb();
  await db.delete('outbox', id);
}

/**
 * 統計 status='pending' 的佇列條目數量。
 * 可用於顯示待同步徽章（Badge）。
 */
export async function countPendingOutbox(): Promise<number> {
  const db = await getOfflineDb();
  return db.countFromIndex('outbox', 'by-status', 'pending');
}

/**
 * 取得所有 status='failed' 的佇列條目（重試耗盡或 409 衝突）。
 * 供離線管理頁展示「同步失敗項」清單，讓用戶重試或放棄。
 */
export async function listFailedOutbox(): Promise<OutboxEntry[]> {
  const db = await getOfflineDb();
  return db.getAllFromIndex('outbox', 'by-status', 'failed');
}

/**
 * 統計 status='failed' 的佇列條目數量。
 * 供全局橫幅顯示失敗徽章，避免資料靜默滯留本機。
 */
export async function countFailedOutbox(): Promise<number> {
  const db = await getOfflineDb();
  return db.countFromIndex('outbox', 'by-status', 'failed');
}

/**
 * 把一筆 failed 條目重新入隊（status→pending、attempts 歸零、清除 lastError），
 * 讓下一輪 syncOnce 重新嘗試。
 *
 * @param opts.dropBodyVersion - 針對 409 版本衝突（如 form_instance PATCH）：
 *   移除 body 中的 version 欄位後重送，後端跳過版本比對，等同「以本機版本覆蓋線上」。
 * @returns 是否成功重新入隊（條目不存在或非 failed 時返回 false）
 */
export async function requeueFailedOutbox(
  id: string,
  opts?: { dropBodyVersion?: boolean },
): Promise<boolean> {
  const db = await getOfflineDb();
  const tx = db.transaction('outbox', 'readwrite');
  const store = tx.objectStore('outbox');
  const cur = await store.get(id);
  if (!cur || cur.status !== 'failed') {
    await tx.done;
    return false;
  }
  let body = cur.body;
  if (opts?.dropBodyVersion && body && typeof body === 'object' && !Array.isArray(body)) {
    const rest = { ...(body as Record<string, unknown>) };
    delete rest.version;
    body = rest;
  }
  await store.put({ ...cur, body, status: 'pending', attempts: 0, lastError: undefined });
  await tx.done;
  return true;
}
